import { useEffect, useRef, useState } from 'react';

/**
 * ReactBits — Counter.
 *
 * Animated count-up that kicks off the first time the element scrolls into
 * view. Uses an ease-out cubic curve and stays at the final value afterwards.
 *
 * Props:
 *   from / to — start + end numbers (defaults 0 → value)
 *   duration  — ms (default 1600)
 *   prefix / suffix — optional decorations (e.g. "+", "%", "°")
 *   formatter — optional (n) => string for custom formatting (e.g. "360°")
 *   className — class hooks
 *   replayKey — when this value changes, the counter resets and re-animates
 *               from `from` to `to`. Used by CardSwap KPIs so the numbers
 *               roll up again every time their card lands at the front.
 *   decimals  — number of decimal places to keep when rendering (default 0)
 */
const easeOutCubic = (p) => 1 - Math.pow(1 - p, 3);

const Counter = ({
  to = 0,
  from = 0,
  duration = 1600,
  prefix = '',
  suffix = '',
  formatter,
  className = '',
  replayKey,
  decimals = 0,
}) => {
  const ref = useRef(null);
  const [value, setValue] = useState(from);
  const startedRef = useRef(false);
  const rafRef = useRef(null);

  // iter165 — Run animation. Exposed so both the IntersectionObserver and the
  // replayKey effect can trigger a fresh roll-up without duplicating logic.
  const run = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setValue(from);
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = easeOutCubic(p);
      setValue(from + (to - from) * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setValue(to);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          run();
          io.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, duration]);

  // iter165 — replayKey changes re-fire the count-up animation. Useful when
  // a CardSwap card lands at front and we want its KPIs to roll again.
  useEffect(() => {
    if (replayKey === undefined) return;
    if (!startedRef.current) return; // wait for first IO trigger
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayKey]);

  const display = formatter
    ? formatter(value)
    : `${prefix}${decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString()}${suffix}`;

  return <span ref={ref} className={className}>{display}</span>;
};

export default Counter;
